import type { AccessibilityOverrides } from "@ff/domain";

/**
 * The resolved accessibility environment, after the stored overrides and the
 * signals the platform can actually report have been folded together.
 *
 * `AccessibilityOverrides` is what the user chose; this is what the UI must
 * render. They are separate types on purpose: `"system"` is not a renderable
 * state, and every component that branched on the raw preference would have to
 * re-resolve it — usually forgetting one of the two system signals.
 */
export interface AccessibilityFlags {
  /**
   * The structural branch (§8.1). Not a size: it collapses multi-pane
   * compositions, forces ordered presentations, and disables the wide sidebar.
   */
  readonly accessibilityTextSize: boolean;
  readonly increaseContrast: boolean;
  readonly differentiateWithoutColor: boolean;
  readonly buttonShapes: boolean;
  readonly reduceMotion: boolean;
}

/**
 * What the rendering environment can report on its own.
 *
 * Only three of the five settings have a system signal at all: Differentiate
 * Without Color and Button Shapes have no media query on any platform, which is
 * exactly why `AccessibilityOverrides` models them as plain booleans rather
 * than as `SystemOverridablePreference`.
 *
 * Reading these signals is `packages/ui/src/layout`'s job (it owns the only
 * `matchMedia` exemption); this module stays pure so the resolution rules can
 * be tested without a DOM.
 */
export interface SystemAccessibilitySignals {
  /**
   * The root-font-size heuristic from §8.1. There is no browser equivalent of
   * `dynamicTypeSize.isAccessibilitySize`, so a user who never opens Settings
   * still gets the collapse if their browser text size is far enough up.
   */
  readonly accessibilityTextSize: boolean;
  readonly increaseContrast: boolean;
  readonly reduceMotion: boolean;
}

export const NO_SYSTEM_ACCESSIBILITY_SIGNALS: SystemAccessibilitySignals = {
  accessibilityTextSize: false,
  increaseContrast: false,
  reduceMotion: false,
};

export const DEFAULT_ACCESSIBILITY_FLAGS: AccessibilityFlags = {
  accessibilityTextSize: false,
  increaseContrast: false,
  differentiateWithoutColor: false,
  buttonShapes: false,
  reduceMotion: false,
};

/**
 * `"system"` defers to the platform; `"on"` forces the treatment on. There is
 * deliberately no `"off"`: every one of these settings only ever *adds* an
 * accommodation, so letting the app override a system preference downward would
 * mean a user who turned Reduce Motion on at the OS level could have it turned
 * back off by an app preference they set months earlier.
 */
export function resolveAccessibilityFlags(
  overrides: AccessibilityOverrides,
  system: SystemAccessibilitySignals = NO_SYSTEM_ACCESSIBILITY_SIGNALS,
): AccessibilityFlags {
  return {
    accessibilityTextSize:
      overrides.textSize === "accessibility" || system.accessibilityTextSize,
    increaseContrast:
      overrides.increaseContrast === "on" || system.increaseContrast,
    differentiateWithoutColor: overrides.differentiateWithoutColor,
    buttonShapes: overrides.buttonShapes,
    reduceMotion: overrides.reduceMotion === "on" || system.reduceMotion,
  };
}

/**
 * The root `data-ff-*` attributes that make `tokens.css` agree with the
 * resolved flags.
 *
 * Only the *overrides* are emitted. A flag that is on because the system
 * reported it already has a matching media query in `tokens.css`, and stamping
 * an attribute for it too would be a second source of truth that drifts the
 * moment the user changes the OS setting with the tab open.
 *
 * A value of `undefined` means "remove the attribute" — the shell applies these
 * to `document.documentElement`, and an attribute whose value is the string
 * `"undefined"` would keep matching the selector.
 */
export function accessibilityRootAttributes(
  overrides: AccessibilityOverrides,
): Readonly<Record<string, string | undefined>> {
  return {
    "data-ff-text-size":
      overrides.textSize === "accessibility" ? "accessibility" : undefined,
    "data-ff-contrast": overrides.increaseContrast === "on" ? "more" : undefined,
    "data-ff-differentiate": overrides.differentiateWithoutColor
      ? "on"
      : undefined,
    "data-ff-button-shapes": overrides.buttonShapes ? "on" : undefined,
    "data-ff-reduce-motion": overrides.reduceMotion === "on" ? "on" : undefined,
  };
}
